import Immutable from 'immutable';

import {
	webapi,
	createModuleState,
	REPORTED_EXCEPTION,
	StaticLookup,
	setPreference,
	getPreference,
	createModelForState
} from 'ps-gui';
import {
	createSearchModel,
	getSqlFulltextFilter,
	listToSet,
} from './utils';
import { getPhrase } from '../data/phrases';
import { EMPTY_LOOKUP, getLookup } from '../data/lookup';

const EMPTY_MAP = Immutable.Map({});
const EMPTY_LIST = Immutable.List([]);
const EMPTY_STATE = createModuleState('UsageReport');


function getPartyRightHolderText(rh) {
	const rightHolderPartyId = rh.rightHolderPartyId;
	if (rightHolderPartyId) {
		let name, id, role, subRole;
		if (rh.roleTypeId === 'HEI' && rh.rightHolderRoleTypeId && rh.rightHolderRoleTypeId !== 'HEI') {
			name = rh.rightHolderPartyName;
			id = rh.rightHolderPartyId;
			role = getPhrase('inheritedRights');
		} else if (rh.roleTypeId === 'PER' && rh.rightHolderRoleTypeId === 'ENS') {
			name = rh.partyName;
			id = rh.rightHolderPartyId;
			role = getPhrase('ensembleShare');
			subRole = rh.rightHolderPartyName;
		} else {
			name = rh.partyName;
			role = rh.roleTypeName.toLowerCase();
			id = role.partyId;
		}
		return name + ' (' + role + (subRole ? ' ' + subRole : '') + ') - ' + id;
	} else {
		return rh.partyName + ' (' + rh.roleTypeName + ') - ' + rh.partyId;
	}
}


/*
	0: role.partyId + '/' +
	1: role.noRel + '/' +
	2: role.roleTypeId + '/' +
	3: role.rightHolderPartyId + '/' +
	4: role.rightHolderRoleTypeId;
*/
function parseUsageRoleId(key) {
	if (!key) {
		return {};
	}
	const tokens = key.split('/');
	return {
		party_id: parseInt(tokens[0]),
		no_rel: tokens[1] ? parseInt(tokens[1]) : null,
		role_type_id: tokens[2] || null,
		right_holder_party_id: parseInt(tokens[3]) || null,
		right_holder_role_type_id: tokens[4] || null
	};
}

function formatUsageRoleId(role) {
	return role.partyId + '/' + (role.noRel ?? '') + '/' + role.roleTypeId + '/' + (role.rightHolderPartyId || '') + '/' + (role.rightHolderRoleTypeId || '');
}

export default class UsageReportModel {
	#data;

	constructor(data) {
		this.#data = data || EMPTY_MAP;
	}

	#getPreferenceKeyBase() {
		return '//usgrep//';
	}

	#getExtFilterPreferenceKey() {
		return this.#getPreferenceKeyBase() + 'extFilter';
	}

	#setProperty(name, value) {
		if (Array.isArray(name)) {
			return new UsageReportModel(this.#data.setIn(name, value));
		} else {
			return new UsageReportModel(this.#data.set(name, value));
		}
	}

	async checkGlobalContextChange(globalContext) {
		if (!globalContext) {
			return this;
		}
		const gcLangId = globalContext.getLanguageId();
		const langId = this.#data.get('langId');
		if (gcLangId === langId) {
			return this;
		}
		return (await this.initialize(globalContext)).#setProperty('languageTimestamp', new Date().getTime());
	}

	getLanguageTimestamp() {
		return this.#data.get('languageTimestamp') || 0;
	}

	setSelectedTabId(value) {
		return this.#setProperty('selectedTabId', value);
	}



	setRoleId(roleId) {
		let model = this.#setProperty('roleId', roleId);
		const dp = this.getDistributionPeriodLookup();
		if (!dp.getText(this.#data.get('distributionPeriodId'))) {
			model = model.setDistributionPeriodId(dp.getItems().getIn([0, 'key']));
		}
		return model;
	}

	setDistributionPeriodId(value) {
		return this.#setProperty('distributionPeriodId', value);
	}

	setSearchCustomResultProps(value) {
		return this.#setProperty('searchCustomResultProps', value);
	}

	setAggregateCustomResultProps(value) {
		return this.#setProperty('aggregateCustomResultProps', value);
	}

	setCmoRewardsCustomResultProps(value) {
		return this.#setProperty('cmoRewardsCustomResultProps', value);
	}

	setRoleLookup(roles) {
		//this.#setProperty('roleLookup', StaticLookup(Immutable.fromJS(pi.roles || [])));
		let luItems = [];
		if (roles) {
			roles.forEach(role => {
				luItems.push({
					key: formatUsageRoleId(role),
					cells: [role.text]
				});
			});
		}
		return this.#setProperty('roleLookup', StaticLookup(Immutable.fromJS(luItems)));
	}

	async initializeLookups() {
		let pi = await webapi.execute('/api/proc/api_usagereport/getpartyinfo');
		if (pi === REPORTED_EXCEPTION) {
			return this;
		}
		// console.log('pi = ', pi);
		let model = this.setRoleLookup(pi.roles);
		let dpIdMap = pi.distributionPeriods || {};
		model = model.#setProperty('distributionPeriodLookup', getLookup('distributionPeriod').clone(key => dpIdMap[key]));
		model = model.#setProperty('stations', processStations(pi.stations));
		model = model.#setProperty('bcRunTypes', Immutable.fromJS(pi.bcRunTypes));

		if (pi?.roles?.length > 0) {
			return model.setRoleId(formatUsageRoleId(pi.roles[0]));
		} else {
			return model;
		}
	}

	getDistributionPeriodLookup() {
		return this.#data.get('distributionPeriodLookup') || EMPTY_LOOKUP;
	}

	initializePreferences() {
		let data = this.#data;
		let ef = getPreference(this.#getExtFilterPreferenceKey());
		if (!ef) {
			return this;
		}
		const roleLookup = data.get('roleLookup');
		if (roleLookup) {
			const roleId = ef.get('roleId');
			if (roleId && roleLookup.getText(roleId)) {
				data = data.set('roleId', roleId);
			}
		}
		const distLookup = this.getDistributionPeriodLookup();
		if (distLookup) {
			const distPeriodId = ef.get('distributionPeriodId') || distLookup.getItems().getIn([0, 'key']);
			data = data.set('distributionPeriodId', distPeriodId);
		}
		return new UsageReportModel(data);

	}

	async initialize(globalContext) {
		let model = await this.initializeLookups();
		let srch = createSearchModel('/api/dq/sv_usage_report')
			.setFixedFilter({ party_id: -1 })
			.setProcessFilter(getProcessSearchFilter(model.getData().get('stations')))
			.setOutput(getSearchOutputColumns());
		srch = srch.initializePreferences();
		model = model.setSearch(srch);

		let agg = createSearchModel('/api/dq/sv_usage_report_aggregate')
			.setFixedFilter({ party_id: -1 })
			.setProcessFilter(processAggregateSearchFilter)
			.setOutput(getAggregateOutputColumns());
		agg = agg.initializePreferences();
		model = model.setAggregate(agg);

		let cmor = createSearchModel('/api/dq/sv_cmo_statement_reward')
			.setFixedFilter({ party_id: -1 })
			.setProcessFilter(processCmoRewardSearchFilter)
			.setOutput(getCmoRewardOutputColumns());
		cmor = cmor.initializePreferences();
		model = model.setCmoRewards(cmor);

		model = model.initializePreferences();
		model = await model.refreshSearch(true);
		return model.#setProperty('langId', globalContext.getLanguageId());
	}

	async refreshSearch(dontSavePreference) {
		const roleId = this.#data.get('roleId');
		if (!roleId) {
			return this;
		}
		const distributionPeriodId = this.#data.get('distributionPeriodId');
		let fixedFilter = parseUsageRoleId(roleId);

		fixedFilter.distribution_period_id = distributionPeriodId;

		if (!dontSavePreference && roleId && distributionPeriodId) {
			setPreference(this.#getExtFilterPreferenceKey(), Immutable.Map({
				roleId,
				distributionPeriodId
			}));
		}

		let agg = this.getAggregate().setFixedFilter(fixedFilter);
		agg = await agg.search(1);
		let srch = this.getSearch().setFixedFilter(fixedFilter);
		srch = await srch.search(1);
		let cmor = this.getCmoRewards().setFixedFilter(fixedFilter);
		cmor = await cmor.search(1);
		return this.setSearch(srch).setAggregate(agg).setCmoRewards(cmor);
	}

	setCmoRewards(model) {
		return this.#setProperty('cmoRewards', model.getState());
	}


	setAggregate(model) {
		return this.#setProperty('aggregate', model.getState());
	}

	setSearch(model) {
		return new UsageReportModel(this.#data.set('search', model.getState()));
	}

	getSearch() {
		return createModelForState(this.#data.get('search'));
	}

	getCmoRewards() {
		return createModelForState(this.#data.get('cmoRewards'));
	}

	getAggregate() {
		return createModelForState(this.#data.get('aggregate'));
	}

	getData() {
		return this.#data;
	}

	getState() {
		return EMPTY_STATE.set('data', this.#data);
	}
}

const _aggregates = [
	'time_used',
	'spins',
	'ip_gross_amount',
	'np_gross_amount'
].map(columnName => {
	return {
		columnName,
		type: 'sum'
	};
});

const _aggregateAggregates = [
	'spins',
	'ip_gross_amount',
	'np_gross_amount'
].map(columnName => {
	return {
		columnName,
		type: 'sum'
	};
});

const _cmoRewardAggregates = [
	'gross_amount',
	'withholding_tax',
	'net_amount'
].map(columnName => {
	return {
		columnName,
		type: 'sum'
	};
});

export function getAggregateOutputColumns(forExport) {
	if (forExport) {
		return [
			'title',
			'subtitle',
			'isrc',
			'recording_id',
			'recording_type_id',
			'spins',
			'ip_gross_amount',
			'np_gross_amount'
		];
	} else {
		return [
			'recording',
			'recording_type_id',
			'bc_run_type_id',
			'spins',
			'ip_gross_amount',
			'np_gross_amount',
		];
	}
}


export function getCmoRewardOutputColumns(forExport) {
	if (forExport) {
		return [
			'from_date',
			'to_date',
			'country',
			'cmo_name',
			'gross_amount',
			'withholding_tax',
			'net_amount',
		];
	} else {
		return [
			'country',
			'period',
			'gross_amount',
			'withholding_tax',
			'net_amount',
		];
	}
}

export function getSearchOutputColumns(forExport) {
	if (forExport) {
		return [
			'title',
			'subtitle',
			'recording_id',
			'isrc',
			'broadcaster',
			'station',
			'usage_date',
			'recording_type_id',
			'time_used',
			'spins',
			'ip_gross_amount',
			'np_gross_amount',
			'is_rerun',
			'is_sale'
		];
	} else {
		return [
			'recording',
			'station',
			'usage_date',
			'recording_type_id',
			'bc_run_type_id',
			'time_used',
			'spins',
			'ip_gross_amount',
			'np_gross_amount'
		];
	}
}

function processAggregateSearchFilter(filter) {
	filter._aggregates = _aggregateAggregates;
	if (filter.recording) {
		filter.recording = getSqlFulltextFilter(filter.recording);
	}
	return filter;
}

function processCmoRewardSearchFilter(filter) {
	filter._aggregates = _cmoRewardAggregates;
	if (filter.recording) {
		filter.recording = getSqlFulltextFilter(filter.recording);
	}
	if (filter.periodFrom) {
		filter.to_dateFrom = filter.periodFrom;
		filter.periodFrom = null;
	}
	if (filter.periodTo) {
		filter.from_dateTo = filter.periodTo;
		filter.periodTo = null;
	}
	return filter;
}

function getProcessSearchFilter(stations) {
	stations = stations || EMPTY_LIST;

	return function (filter) {
		if (filter.recording) {
			filter.recording = getSqlFulltextFilter(filter.recording);
		}
		filter._aggregates = _aggregates;

		if (filter.station) {
			const selectedStations = listToSet(filter.station);
			filter.station = null;
			let af = {
				type: 'or',
				nodes: []
			};
			let broadcasterPartyId = [];
			stations.forEach(parent => {
				const parentId = parent.get('parentId');
				if (selectedStations[parentId]) {
					broadcasterPartyId.push(parentId);
				} else {
					const children = parent.get('children');
					if (children && children.size > 0) {
						let childIds = [];
						children.forEach(child => {
							const childId = child.get('childId');
							if (selectedStations[childId]) {
								childIds.push(childId);
							}
						});
						if (childIds.length > 0) {
							af.nodes.push({
								type: 'and',
								nodes: [{
									columnName: 'broadcaster_party_id',
									operator: '=',
									value: parentId
								}, {
									columnName: 'station',
									operator: 'in',
									value: childIds
								}]
							});
						}
					}
				}
			});
			if (broadcasterPartyId.length > 0) {
				af.nodes.push({
					columnName: 'broadcaster_party_id',
					operator: 'in',
					value: broadcasterPartyId
				});
			}
			if (af.nodes.length > 0) {
				filter._advancedFilter = af;
			}
		}
		return filter;
	};
}

function processStations(rows) {
	let res = [];
	let prevParentId = null;
	let currParent = null;
	rows = rows || [];
	rows.forEach(row => {
		const parentId = row.broadcasterPartyId;
		const parentText = row.broadcasterName;
		const childId = row.station;
		if (!parentId) {
			return;
		}
		const childText = row.station;
		if (parentId !== prevParentId || !currParent) {
			prevParentId = parentId;
			currParent = {
				parentId,
				text: parentText,
				children: []
			};
			res.push(currParent);
		}
		if (childId) {
			currParent.children.push({
				parentId,
				childId,
				text: childText
			});
		}
	});
	return Immutable.fromJS(res);
}

